import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Button, Select, Table, Tag } from '@grovus/design-system'
import { NO_VALUE } from '../creatives/format'
import type { AdminCopy } from '../i18n'
import {
  changeClaim, fetchAdminCompanies, fetchPortfolios, requestFacts, requestRediscovery, retryMetaRequest,
  type FactsSummary, type ListedAccount, type MetaPortfolio, type MetaRequest, type PortfolioAccount,
  type PortfoliosOverview, type Sent,
} from './api'
import { formatWhen } from './format'

interface Props {
  copy: AdminCopy
  narrow: boolean
  /** Every `denied` answer lands here. */
  onLeave: () => void
}

type View = { status: 'loading' } | { status: 'failed' } | { status: 'ok'; overview: PortfoliosOverview }

const BORDER = '1px solid var(--mantine-color-default-border)'
const FRAME: CSSProperties = { border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }
const TOP: CSSProperties = { verticalAlign: 'top' }
const SMALL: CSSProperties = { fontSize: 'var(--mantine-font-size-sm)', textWrap: 'pretty', overflowWrap: 'anywhere' }
const STRIPE = 'light-dark(var(--mantine-color-ink-0), var(--mantine-color-dark-6))'
/** How often an active request is read again; the worker moves it once per tick. */
const POLL_MS = 30_000
/** A budget wait has no next attempt time: the next tick, five minutes on. */
const BUDGET_WAIT_MS = 300_000

function Muted({ children }: { children: ReactNode }) {
  return <span className="grv-muted">{children}</span>
}

/**
 * The disclosure arrow: right when the panel it opens is closed, down when it is open.
 *
 * The span owns its box (`display: 'inline-block'` plus `verticalAlign: 'middle'`) rather
 * than borrowing one from a flex parent. The account rows inside the panel are flex, but
 * the facts cell in the table is not, and an inline span there dropped its width, height
 * and rotation and drew nothing at all. The callers supply only their own margins.
 */
function Chevron({ open, color, style }: { open: boolean; color?: string; style?: CSSProperties }) {
  return (
    <span
      aria-hidden="true"
      data-testid="facts-chevron"
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        flex: 'none',
        width: 7,
        height: 7,
        borderRight: `2px solid ${color ?? 'light-dark(var(--mantine-color-ink-6), var(--mantine-color-dark-2))'}`,
        borderBottom: `2px solid ${color ?? 'light-dark(var(--mantine-color-ink-6), var(--mantine-color-dark-2))'}`,
        transform: open ? 'rotate(45deg)' : 'rotate(-45deg)',
        ...style,
      }}
    />
  )
}

const active = (request: MetaRequest | null) =>
  request !== null && (request.state === 'queued' || request.state === 'running' || request.state === 'waiting')

/** When to read the request again: every 30 s while it moves, at its next attempt while it waits. */
export function refetchIn(request: MetaRequest | null, now: number = Date.now()): number | null {
  if (request === null || !active(request)) return null
  if (request.state !== 'waiting') return POLL_MS
  if (request.nextAttemptAt === null) return BUDGET_WAIT_MS
  return Math.max(POLL_MS, new Date(request.nextAttemptAt).getTime() - now)
}

/** The soonest read any of the requests asks for, or `null` while none is active. */
function soonest(requests: Array<MetaRequest | null>): number | null {
  const waits = requests.map((request) => refetchIn(request)).filter((wait) => wait !== null)
  return waits.length === 0 ? null : Math.min(...waits)
}

/** The requests the page polls for: the rediscovery and every account's facts request. */
const watched = (overview: PortfoliosOverview | null): Array<MetaRequest | null> =>
  overview === null ? [] : [overview.rediscovery, ...overview.portfolios.flatMap((p) => p.listed.map((a) => a.facts.request))]

type FactsState = 'observed' | 'absent' | 'unknown'

/** Observed or absent from a complete collection; unknown without one (D9). */
export function factsState(facts: FactsSummary): FactsState {
  if (facts.complete === null) return 'unknown'
  return facts.complete.omniPurchase ? 'observed' : 'absent'
}

const STATE_ORDER: FactsState[] = ['observed', 'absent', 'unknown']

/** "2 July 2026" for a stored `YYYY-MM-DD` day, with no time zone shift. */
function calendarDay(day: string, copy: AdminCopy): string {
  const [year, month, date] = day.split('-').map(Number)
  return `${date} ${copy.months[month - 1]} ${year}`
}

function StateTag({ state, copy, count }: { state: FactsState; copy: AdminCopy; count?: number }) {
  const text = count === undefined ? copy[state] : `${copy[state]} ${count}`
  if (state === 'observed') return <Tag color="brand">{text}</Tag>
  if (state === 'absent') return <Tag>{text}</Tag>
  return <Tag><span className="grv-muted">{text}</span></Tag>
}

/** Screen 16's facts cell: the portfolio's accounts counted by state. */
function FactsCounts({ portfolio, copy }: { portfolio: MetaPortfolio; copy: AdminCopy }) {
  const states = portfolio.listed.map((account) => factsState(account.facts))
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {STATE_ORDER.map((state) => {
        const count = states.filter((s) => s === state).length
        return count === 0 ? null : <StateTag key={state} state={state} copy={copy} count={count} />
      })}
    </span>
  )
}
/**
 * The request card's tag and lines, as screen 16 draws each state; `lines` replaces the
 * rediscovery's lines for a facts request.
 */
function RequestCard({ request, copy, onRetry, retrying, disabled, lines: own }: {
  request: MetaRequest; copy: AdminCopy; onRetry: () => void; retrying: boolean; disabled: boolean; lines?: string[]
}) {
  const when = (iso: string | null) => (iso === null ? '' : formatWhen(iso, copy))
  let tag: ReactNode
  let lines: string[]
  switch (request.state) {
    case 'queued':
      tag = <Tag>{copy.queued}</Tag>
      lines = [copy.requestedAtBy(when(request.requestedAt), request.requestedByEmail), copy.waitingForWorker]
      break
    case 'running':
      tag = <Tag color="brand">{copy.running}</Tag>
      lines = [copy.startedPage(when(request.startedAt ?? request.requestedAt), request.pages + 1, request.runs)]
      break
    case 'waiting':
      tag = <Tag>{copy.waitingState}</Tag>
      lines = [copy.waitReasons[request.waitReason ?? 'budget']]
      if (request.nextAttemptAt !== null) lines.push(copy.nextAttempt(when(request.nextAttemptAt)))
      break
    case 'done':
      tag = <Tag>{copy.done}</Tag>
      lines = [copy.finishedStaged(when(request.finishedAt), request.stagedPortfolios, request.stagedAccounts)]
      break
    case 'failed':
      tag = <Tag color="bad">{copy.failed}</Tag>
      lines = [copy.failuresLastError(request.failures, request.lastError ?? ''), copy.lastCompleteListing]
      break
  }
  if (own !== undefined) lines = own
  return (
    <div data-testid="request-card" style={{ ...FRAME, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, padding: '12px 16px', maxWidth: '100%' }}>
      {tag}
      {lines.map((line) => <span key={line} className="grv-muted" style={SMALL}>{line}</span>)}
      {request.state === 'failed' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px' }}>
          <Button variant="default" size="compact-sm" onClick={onRetry} loading={retrying} disabled={disabled}>{copy.retry}</Button>
          <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{copy.retryNote}</span>
        </div>
      )}
    </div>
  )
}

function Accounts({ portfolio, copy }: { portfolio: MetaPortfolio; copy: AdminCopy }) {
  return (
    <>
      <span className="grv-num">{portfolio.accounts}</span>
      {portfolio.connected > 0 && <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)', whiteSpace: 'nowrap' }}>{copy.connectedCount(portfolio.connected)}</span>}
    </>
  )
}

function Name({ portfolio }: { portfolio: MetaPortfolio }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{portfolio.businessName ?? portfolio.businessId}</span>
      <span className="grv-muted grv-num" style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{portfolio.businessId}</span>
    </div>
  )
}

function Claim({ portfolio, copy }: { portfolio: MetaPortfolio; copy: AdminCopy }) {
  return portfolio.claimedBy === null ? <Muted>{copy.unclaimed}</Muted> : <span style={{ fontWeight: 500 }}>{portfolio.claimedBy.name}</span>
}

/** The panel open under a portfolio row (screen 17). */
type Panel =
  | { kind: 'claim'; businessId: string }
  | { kind: 'unclaim'; businessId: string; company: { id: string; name: string } }
  | { kind: 'refused'; businessId: string; company: string; accounts: PortfolioAccount[] }
  | { kind: 'facts'; businessId: string }

type Companies = { status: 'loading' } | { status: 'failed' } | { status: 'ok'; list: Array<{ value: string; label: string }> }

const portfolioName = (portfolio: MetaPortfolio) => portfolio.businessName ?? portfolio.businessId

/** The refusal line a claim or unclaim answer shows in its open panel. */
function refusalLine(result: Sent<PortfoliosOverview>, copy: AdminCopy): string {
  const r = copy.claimRefusals
  if (result.status === 'notFound') return r.notFound
  if (result.status !== 'refused') return r.failed
  switch (result.code) {
    case 'claimed_elsewhere': return r.claimedElsewhere(result.company.name)
    case 'company_frozen': return r.frozen
    case 'unknown_company': return r.unknownCompany
    case 'demo_company': return r.demo
    case 'not_claimed': return r.notClaimed
    default: return r.failed
  }
}

function PanelFrame({ narrow, children }: { narrow: boolean; children: ReactNode }) {
  return <div data-testid="portfolio-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: narrow ? 16 : '16px 24px', minWidth: 0 }}>{children}</div>
}

function PanelError({ message }: { message: string | null }) {
  return message === null ? null : <span className="grv-muted" role="alert" style={SMALL}>{message}</span>
}

/** What the facts rows send: a refresh of an account, or a retry of its failed request. */
export interface FactsActions {
  /** The account whose request is in flight. */
  sending: string | null
  /** A claim change is in flight, and its answer comes first. */
  held: boolean
  /** A refusal line under the account it belongs to. */
  error: { account: string; message: string } | null
  onRefresh: (account: string) => void
  onRetry: (account: string, request: string) => void
}

/** A facts request's lines: the day reached while it runs, the days covered when done. */
function factsLines(request: MetaRequest, facts: FactsSummary, copy: AdminCopy): string[] | undefined {
  const when = (iso: string | null) => (iso === null ? '' : formatWhen(iso, copy))
  switch (request.state) {
    case 'running':
      return [copy.factsStarted(when(request.startedAt ?? request.requestedAt), facts.collectingDays ?? 0, request.runs)]
    case 'done':
      return [copy.factsFinished(when(request.finishedAt), facts.complete?.coveredDays ?? 90)]
    case 'failed':
      return [copy.failuresLastError(request.failures, request.lastError ?? '')]
    default:
      return undefined
  }
}

function FactRow({ label, narrow, children }: { label: ReactNode; narrow: boolean; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '220px minmax(0, 1fr)', gap: '2px 16px', alignItems: 'baseline' }}>
      {label}
      {children}
    </div>
  )
}

/** One account of screen 17: the summary line, Refresh facts, its request and, expanded, the facts. */
function AccountFactsRow({ account, copy, narrow, actions }: { account: ListedAccount; copy: AdminCopy; narrow: boolean; actions: FactsActions }) {
  const [expanded, setExpanded] = useState(false)
  const { facts } = account
  const { complete, collectingDays, request } = facts
  const state = factsState(facts)
  const busy = active(request)
  const sending = actions.sending === account.platformAccountId
  let summary: string
  if (complete !== null) summary = `${copy[state]} · ${copy.daysOf90(complete.coveredDays)} · ${copy.collectedOn(formatWhen(complete.finishedAt, copy))}`
  else if (collectingDays !== null) summary = `${copy.unknown} · ${copy.dayOf90(collectingDays)} · ${copy.collecting}`
  else summary = `${copy.unknown} · ${copy.notCollected}`
  const muted = (text: string) => <span className="grv-muted">{text}</span>
  const label = (text: string) => <span className="grv-label">{text}</span>
  const objectives = complete === null
    ? muted(NO_VALUE)
    : (
        <span className="grv-num" style={{ overflowWrap: 'anywhere' }}>
          {complete.objectives.length === 0 ? copy.noCampaigns : complete.objectives.map((o) => `${o.objective} ${o.campaigns}`).join(' · ')}
          {complete.objectivesTruncated && <span className="grv-muted">{` · ${copy.campaignsTruncated}`}</span>}
        </span>
      )
  let completeness: ReactNode
  if (complete !== null) completeness = <span className="grv-num">{copy.daysOf90(complete.coveredDays)}</span>
  else if (collectingDays !== null) completeness = <span><span className="grv-num">{copy.dayOf90(collectingDays)}</span><span className="grv-muted">{` · ${copy.collecting}`}</span></span>
  else completeness = muted(copy.notCollected)
  // The row's chevron keeps the explicit border pair and the flex margins it was drawn
  // with, so the shared component changes nothing about how the panel looks.
  const chevron: CSSProperties = { margin: expanded ? '5px 5px 0 2px' : '8px 5px 0 2px' }
  return (
    <div data-testid="facts-account" style={{ ...FRAME, overflow: 'hidden' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '10px 16px', padding: '12px 16px' }}>
        <button
          type="button" aria-expanded={expanded} onClick={() => setExpanded((open) => !open)}
          style={{ flex: narrow ? '1 1 100%' : '1 1 260px', minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 10, background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer' }}
        >
          <Chevron open={expanded} style={chevron} />
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 10px' }}>
              <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{account.name}</span>
              <span className="grv-muted grv-num" style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{account.platformAccountId}</span>
            </span>
            <span className="grv-muted" style={SMALL}>{summary}</span>
          </span>
        </button>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '8px 12px', flex: narrow ? '1 1 100%' : 'none', maxWidth: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <Button variant="default" size="compact-sm" disabled={busy || actions.sending !== null || actions.held} loading={sending && !busy} onClick={() => actions.onRefresh(account.platformAccountId)}>{copy.refreshFacts}</Button>
            {busy && <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-xs)' }}>{copy.alreadyRequested}</span>}
          </div>
          {request && (
            <div style={{ width: narrow ? '100%' : 320, maxWidth: '100%' }}>
              <RequestCard
                request={request} copy={copy} lines={factsLines(request, facts, copy)} retrying={sending} disabled={actions.sending !== null || actions.held}
                onRetry={() => actions.onRetry(account.platformAccountId, request.id)}
              />
            </div>
          )}
        </div>
        {actions.error?.account === account.platformAccountId && <span style={{ flex: '1 1 100%' }}><PanelError message={actions.error.message} /></span>}
      </div>
      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 16px 16px', borderTop: BORDER }}>
          <FactRow label={label(copy.campaignObjectives)} narrow={narrow}>{objectives}</FactRow>
          <FactRow label={label(copy.mainActionTypes)} narrow={narrow}>
            {complete === null || complete.mainActionTypes.length === 0 ? muted(NO_VALUE) : <span className="grv-code" style={{ overflowWrap: 'anywhere' }}>{complete.mainActionTypes.join(', ')}</span>}
          </FactRow>
          <FactRow label={<span className="grv-code">omni_purchase</span>} narrow={narrow}><div><StateTag state={state} copy={copy} /></div></FactRow>
          <FactRow label={label(copy.factsWindow)} narrow={narrow}>
            {complete === null ? muted(NO_VALUE) : <span className="grv-num">{copy.windowRange(calendarDay(complete.windowSince, copy), calendarDay(complete.windowUntil, copy), complete.timezoneName)}</span>}
          </FactRow>
          <FactRow label={label(copy.collected)} narrow={narrow}>
            {complete === null ? muted(NO_VALUE) : <span className="grv-num">{formatWhen(complete.finishedAt, copy)}</span>}
          </FactRow>
          <FactRow label={label(copy.completeness)} narrow={narrow}>{completeness}</FactRow>
        </div>
      )}
    </div>
  )
}

/** Screen 17's conversion facts of one portfolio's listed accounts. */
function AccountFacts({ portfolio, copy, narrow, actions }: { portfolio: MetaPortfolio; copy: AdminCopy; narrow: boolean; actions: FactsActions }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span className="grv-label">{copy.conversionFacts}</span>
      {portfolio.listed.map((account) => <AccountFactsRow key={account.platformAccountId} account={account} copy={copy} narrow={narrow} actions={actions} />)}
    </div>
  )
}

/** The facts of any portfolio, claimed or not, with no claim control (D10). */
function FactsPanel({ portfolio, copy, narrow, actions, onClose }: { portfolio: MetaPortfolio; copy: AdminCopy; narrow: boolean; actions: FactsActions; onClose: () => void }) {
  return (
    <PanelFrame narrow={narrow}>
      <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{portfolioName(portfolio)}</span>
      <AccountFacts portfolio={portfolio} copy={copy} narrow={narrow} actions={actions} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button variant="default" size="sm" onClick={onClose}>{copy.close}</Button>
      </div>
    </PanelFrame>
  )
}

/** Screen 17's claim panel: the company, the listed accounts with their facts, Claim. */
function ClaimPanel({ portfolio, copy, narrow, saving, held, error, actions, onClaim, onCancel, onLeave }: {
  /** `held`: a rediscovery, retry or facts request is in flight, and its answer comes first. */
  portfolio: MetaPortfolio; copy: AdminCopy; narrow: boolean; saving: boolean; held: boolean; error: string | null
  actions: FactsActions; onClaim: (companyId: string) => void; onCancel: () => void; onLeave: () => void
}) {
  const [companies, setCompanies] = useState<Companies>({ status: 'loading' })
  const [company, setCompany] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    void fetchAdminCompanies().then((result) => {
      if (!live) return
      if (result.status === 'denied') { onLeave(); return }
      if (result.status !== 'ok') { setCompanies({ status: 'failed' }); return }
      const list = result.body
        .filter((c) => c.status === 'active' && !c.isDemo)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ value: c.id, label: c.name }))
      setCompanies({ status: 'ok', list })
    })
    return () => { live = false }
  }, [onLeave])
  const lead = copy.claimLead(portfolioName(portfolio))
  return (
    <PanelFrame narrow={narrow}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px' }}>
        <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{lead}</span>
        <div style={{ width: narrow ? '100%' : 320, maxWidth: '100%' }}>
          <Select
            size="sm"
            aria-label={copy.claimCompany}
            placeholder={copy.claimCompany}
            data={companies.status === 'ok' ? companies.list : []}
            value={company}
            onChange={setCompany}
            allowDeselect={false}
            searchable
            comboboxProps={{ withinPortal: false }}
            disabled={companies.status !== 'ok'}
          />
        </div>
      </div>
      {companies.status === 'failed' && <span className="grv-muted" role="alert" style={SMALL}>{copy.companiesFailed}</span>}
      {companies.status === 'ok' && companies.list.length === 0 && <Muted>{copy.noClaimableCompany}</Muted>}
      <AccountFacts portfolio={portfolio} copy={copy} narrow={narrow} actions={actions} />
      {portfolio.listed.some((account) => factsState(account.facts) === 'unknown') && (
        <span className="grv-muted" style={{ ...SMALL, maxWidth: 720 }}>{copy.manualCheck}</span>
      )}
      <PanelError message={error} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button size="sm" disabled={company === null || held} loading={saving} onClick={() => company !== null && onClaim(company)}>{copy.claim}</Button>
        <Button variant="default" size="sm" onClick={onCancel}>{copy.cancel}</Button>
      </div>
    </PanelFrame>
  )
}

function UnclaimPanel({ portfolio, company, copy, narrow, saving, held, error, onUnclaim, onCancel }: {
  portfolio: MetaPortfolio; company: string; copy: AdminCopy; narrow: boolean; saving: boolean; held: boolean; error: string | null
  onUnclaim: () => void; onCancel: () => void
}) {
  return (
    <PanelFrame narrow={narrow}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 720 }}>
        <span style={{ fontWeight: 600, textWrap: 'pretty', overflowWrap: 'anywhere' }}>{copy.unclaimTitle(portfolioName(portfolio), company)}</span>
        <span className="grv-muted" style={{ textWrap: 'pretty' }}>{copy.unclaimText(company)}</span>
      </div>
      <PanelError message={error} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button variant="default" size="sm" c="bad.6" disabled={held} loading={saving} onClick={onUnclaim}>{copy.unclaim}</Button>
        <Button variant="default" size="sm" onClick={onCancel}>{copy.cancel}</Button>
      </div>
    </PanelFrame>
  )
}

function RefusedPanel({ portfolio, company, accounts, copy, narrow, onClose }: {
  portfolio: MetaPortfolio; company: string; accounts: PortfolioAccount[]; copy: AdminCopy; narrow: boolean; onClose: () => void
}) {
  return (
    <PanelFrame narrow={narrow}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 720 }}>
        <span style={{ fontWeight: 600, textWrap: 'pretty', overflowWrap: 'anywhere' }}>{copy.unclaimRefusedTitle(portfolioName(portfolio), company)}</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {accounts.map((account) => (
            <span key={account.platformAccountId} data-testid="blocking-account" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 10px' }}>
              <span className="grv-code">{account.platformAccountId}</span><span>{account.name}</span>
            </span>
          ))}
        </div>
        <span className="grv-muted" style={{ textWrap: 'pretty' }}>{copy.unclaimRefusedText}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button variant="default" size="sm" onClick={onClose}>{copy.close}</Button>
      </div>
    </PanelFrame>
  )
}

/**
 * Screen 16 (GRO-141): the portfolios the published discovery lists, unclaimed first, the
 * time of the last discovery and the rediscovery request with its state. Screen 17
 * (GRO-144): Claim and Unclaim open a panel under their row. GRO-145: the facts cell opens
 * the facts panel, and both it and the claim panel show each account's conversion facts.
 *
 * Every overview read takes a number when it starts, and a claim change takes the next one
 * and holds the polling until it answers: a read that started before the change is
 * dropped when it lands, so it cannot show the claim as it was. A claim change and a
 * rediscovery, retry or facts request never run at once, so neither answer can overwrite
 * the other's.
 */
export function PortfoliosPage({ copy, narrow, onLeave }: Props) {
  const [view, setView] = useState<View>({ status: 'loading' })
  const [sending, setSending] = useState(false)
  const [sendFailed, setSendFailed] = useState(false)
  const [panel, setPanel] = useState<Panel | null>(null)
  const [saving, setSaving] = useState(false)
  const [panelError, setPanelError] = useState<string | null>(null)
  const [factsSending, setFactsSending] = useState<string | null>(null)
  const [factsError, setFactsError] = useState<FactsActions['error']>(null)
  const mounted = useRef(true)
  const reads = useRef(0)
  const changing = useRef(false)

  const load = useCallback(async (quiet: boolean) => {
    if (!quiet) setView({ status: 'loading' })
    const read = ++reads.current
    const result = await fetchPortfolios()
    if (!mounted.current || read !== reads.current) return
    if (result.status === 'ok') setView({ status: 'ok', overview: result.body })
    else if (result.status === 'denied') onLeave()
    else if (!quiet && result.status !== 'stale' && result.status !== 'expired') setView({ status: 'failed' })
  }, [onLeave])

  useEffect(() => {
    mounted.current = true
    void load(false)
    return () => { mounted.current = false }
  }, [load])

  const request = view.status === 'ok' ? view.overview.rediscovery : null
  const requests = watched(view.status === 'ok' ? view.overview : null)
  const polled = useRef(requests)
  polled.current = requests
  const pollKey = requests.filter(active).map((r) => `${r?.id}:${r?.state}:${r?.nextAttemptAt}`).join(' ')
  // A read that changes an active request re-runs this effect; one that does not (a failed
  // read, a hidden tab, the same answer) arms the next read itself, so polling never stops.
  useEffect(() => {
    const first = soonest(polled.current)
    if (first === null) return
    let live = true
    let reading = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const arm = (wait: number) => { timer = setTimeout(() => void fire(), wait) }
    const fire = async () => {
      reading = true
      if (document.visibilityState !== 'hidden' && !changing.current) await load(true)
      reading = false
      if (live) arm(soonest(polled.current) ?? POLL_MS)
    }
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || reading) return
      clearTimeout(timer)
      void fire()
    }
    arm(first)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live = false
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [pollKey, load])

  async function send(action: () => ReturnType<typeof requestRediscovery>) {
    ++reads.current
    setSending(true)
    setSendFailed(false)
    const result = await action()
    if (!mounted.current) return
    setSending(false)
    if (result.status === 'ok') {
      ++reads.current
      setView((current) => (current.status === 'ok' ? { status: 'ok', overview: { ...current.overview, rediscovery: result.body } } : current))
    } else if (result.status === 'denied') onLeave()
    else if (result.status !== 'stale' && result.status !== 'expired') setSendFailed(true)
  }

  function open(next: Panel | null) {
    setPanel(next)
    setPanelError(null)
    setFactsError(null)
  }

  /** A facts request or retry for one account; its answer replaces that account's request. */
  async function sendFacts(account: string, action: () => Promise<Sent<MetaRequest>>) {
    ++reads.current
    setFactsSending(account)
    setFactsError(null)
    const result = await action()
    if (!mounted.current) return
    setFactsSending(null)
    if (result.status === 'ok') {
      ++reads.current
      const placed = (portfolio: MetaPortfolio): MetaPortfolio => ({
        ...portfolio,
        listed: portfolio.listed.map((a) => (a.platformAccountId === account ? { ...a, facts: { ...a.facts, request: result.body } } : a)),
      })
      setView((current) => (current.status === 'ok' ? { status: 'ok', overview: { ...current.overview, portfolios: current.overview.portfolios.map(placed) } } : current))
    } else if (result.status === 'denied') onLeave()
    else if (result.status === 'notFound') setFactsError({ account, message: copy.accountGone })
    else if (result.status === 'refused' && result.code === 'company_frozen') setFactsError({ account, message: copy.claimRefusals.frozen })
    else if (result.status !== 'stale' && result.status !== 'expired') setFactsError({ account, message: copy.factsFailed })
  }

  const factsActions: FactsActions = {
    sending: factsSending,
    held: saving,
    error: factsError,
    onRefresh: (account) => void sendFacts(account, () => requestFacts(account)),
    onRetry: (account, id) => void sendFacts(account, () => retryMetaRequest(id)),
  }

  function factsCell(portfolio: MetaPortfolio) {
    if (portfolio.listed.length === 0) return <Muted>{NO_VALUE}</Muted>
    const shown = panel?.kind === 'facts' && panel.businessId === portfolio.businessId
    return (
      // The hover fill and the ring live in `.grv-disclosure`, because inline styles cannot
      // express `:hover` or `:focus-visible`. The inline `color` and `background` reset are
      // gone for the same reason: an inline value beats a stylesheet rule on `:hover`, so
      // the tint could never paint. The reset itself moved into the class.
      <button
        type="button" className="grv-disclosure" aria-expanded={shown}
        aria-label={`${copy.conversionFacts}: ${portfolioName(portfolio)}`}
        onClick={() => open(shown ? null : { kind: 'facts', businessId: portfolio.businessId })}
        style={{ border: 0, padding: 0, font: 'inherit', textAlign: 'left' }}
      >
        <Chevron open={shown} color="currentColor" style={{ marginRight: 6 }} />
        <FactsCounts portfolio={portfolio} copy={copy} />
      </button>
    )
  }

  async function change(action: 'claim' | 'unclaim', portfolio: MetaPortfolio, company: { id: string; name: string }) {
    changing.current = true
    ++reads.current
    setSaving(true)
    setPanelError(null)
    const result = await changeClaim(action, portfolio.businessId, company.id)
    changing.current = false
    if (!mounted.current) return
    setSaving(false)
    if (result.status === 'ok') {
      ++reads.current
      setView({ status: 'ok', overview: result.body })
      open(null)
    } else if (result.status === 'denied') onLeave()
    else if (result.status === 'refused' && result.code === 'claim_blocked') {
      open({ kind: 'refused', businessId: portfolio.businessId, company: company.name, accounts: result.accounts })
    } else if (result.status !== 'stale' && result.status !== 'expired') setPanelError(refusalLine(result, copy))
  }

  function action(portfolio: MetaPortfolio) {
    const claimed = portfolio.claimedBy
    return claimed === null
      ? <Button variant="default" size="compact-sm" disabled={saving} onClick={() => open({ kind: 'claim', businessId: portfolio.businessId })}>{copy.claim}</Button>
      : <Button variant="default" size="compact-sm" c="bad.6" disabled={saving} onClick={() => open({ kind: 'unclaim', businessId: portfolio.businessId, company: claimed })}>{copy.unclaim}</Button>
  }

  function panelFor(portfolio: MetaPortfolio) {
    if (panel === null || panel.businessId !== portfolio.businessId) return null
    const close = () => open(null)
    switch (panel.kind) {
      case 'claim':
        return (
          <ClaimPanel
            portfolio={portfolio} copy={copy} narrow={narrow} saving={saving} held={sending || factsSending !== null} error={panelError} actions={factsActions} onCancel={close} onLeave={onLeave}
            onClaim={(companyId) => void change('claim', portfolio, { id: companyId, name: '' })}
          />
        )
      case 'unclaim': {
        const company = panel.company
        return (
          <UnclaimPanel
            portfolio={portfolio} company={company.name} copy={copy} narrow={narrow} saving={saving} held={sending || factsSending !== null} error={panelError}
            onCancel={close} onUnclaim={() => void change('unclaim', portfolio, company)}
          />
        )
      }
      case 'refused':
        return <RefusedPanel portfolio={portfolio} company={panel.company} accounts={panel.accounts} copy={copy} narrow={narrow} onClose={close} />
      case 'facts':
        return <FactsPanel portfolio={portfolio} copy={copy} narrow={narrow} actions={factsActions} onClose={close} />
    }
  }

  const busy = active(request)
  const overview = view.status === 'ok' ? view.overview : null

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 10px' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--mantine-font-size-xl)', fontWeight: 700, letterSpacing: '-0.01em' }}>{copy.navPortfolios}</h2>
          {overview && <span className="grv-muted grv-num" style={{ fontSize: 'var(--mantine-font-size-xl)', fontWeight: 500 }}>{overview.portfolios.length}</span>}
        </div>
        <span className="grv-muted" style={{ textWrap: 'pretty', maxWidth: 760 }}>{copy.portfoliosSub}</span>
      </div>

      {view.status === 'failed' && (
        <div style={{ ...FRAME, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 16px', padding: 24 }}>
          <span className="grv-muted" role="alert">{copy.portfoliosFailed}</span>
          <Button variant="default" onClick={() => void load(false)}>{copy.retry}</Button>
        </div>
      )}

      {overview && (
        <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', flexWrap: 'wrap', alignItems: narrow ? 'stretch' : 'flex-start', gap: '12px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
            <span className="grv-muted">
              {copy.lastDiscovery} <span className="grv-num">{overview.lastDiscoveryAt === null ? copy.never : formatWhen(overview.lastDiscoveryAt, copy)}</span>
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px' }}>
              <Button variant="default" size="sm" disabled={busy || saving} loading={sending && !busy} onClick={() => void send(requestRediscovery)}>{copy.rediscover}</Button>
              {busy && <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{copy.rediscoverBusy}</span>}
            </div>
            {sendFailed && <span className="grv-muted" role="alert" style={SMALL}>{copy.rediscoverFailed}</span>}
          </div>
          {request && (
            <div style={{ marginLeft: narrow ? 0 : 'auto', width: narrow ? '100%' : 440, maxWidth: '100%' }}>
              <RequestCard request={request} copy={copy} retrying={sending} disabled={saving} onRetry={() => void send(() => retryMetaRequest(request.id))} />
            </div>
          )}
        </div>
      )}

      {overview && overview.portfolios.length === 0 && (
        <div style={{ ...FRAME, padding: 24 }}><Muted>{copy.noPortfolios}</Muted></div>
      )}

      {overview && overview.portfolios.length > 0 && !narrow && (
        <div style={{ ...FRAME, overflow: 'hidden' }}>
          <Table striped stripedColor={STRIPE}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th><span className="grv-label">{copy.portfolio}</span></Table.Th>
                <Table.Th style={{ textAlign: 'right' }}><span className="grv-label">{copy.accounts}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.claimColumn}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.conversionFacts}</span></Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {overview.portfolios.map((portfolio) => {
                const opened = panelFor(portfolio)
                return (
                  <Fragment key={portfolio.businessId}>
                    <Table.Tr data-testid="portfolio-row">
                      <Table.Td style={TOP}><Name portfolio={portfolio} /></Table.Td>
                      <Table.Td style={{ ...TOP, textAlign: 'right' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}><Accounts portfolio={portfolio} copy={copy} /></div>
                      </Table.Td>
                      <Table.Td style={TOP}><Claim portfolio={portfolio} copy={copy} /></Table.Td>
                      <Table.Td style={TOP}>{factsCell(portfolio)}</Table.Td>
                      <Table.Td style={{ ...TOP, textAlign: 'right' }}>{action(portfolio)}</Table.Td>
                    </Table.Tr>
                    {opened && (
                      <Table.Tr bg="var(--mantine-color-body)">
                        <Table.Td colSpan={5} style={{ padding: 0 }}>{opened}</Table.Td>
                      </Table.Tr>
                    )}
                  </Fragment>
                )
              })}
            </Table.Tbody>
          </Table>
        </div>
      )}

      {overview && overview.portfolios.length > 0 && narrow && (
        <div style={{ ...FRAME, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {overview.portfolios.map((portfolio, index) => {
            const opened = panelFor(portfolio)
            return (
              <div key={portfolio.businessId} style={{ borderTop: index === 0 ? 'none' : BORDER, background: index % 2 === 0 ? STRIPE : undefined }}>
                <div data-testid="portfolio-row" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px' }}>
                  <Name portfolio={portfolio} />
                  <div style={{ display: 'grid', gridTemplateColumns: '104px minmax(0, 1fr)', gap: '6px 12px', alignItems: 'baseline' }}>
                    <span className="grv-label">{copy.accounts}</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 8px' }}><Accounts portfolio={portfolio} copy={copy} /></div>
                    <span className="grv-label">{copy.claimColumn}</span>
                    <div><Claim portfolio={portfolio} copy={copy} /></div>
                    <span className="grv-label">{copy.conversionFacts}</span>
                    <div>{factsCell(portfolio)}</div>
                  </div>
                  <div>{action(portfolio)}</div>
                </div>
                {opened && <div style={{ borderTop: BORDER, background: 'var(--mantine-color-body)' }}>{opened}</div>}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
